import { Helmet } from 'react-helmet-async';
import {
  SITE_URL,
  SITE_NAME,
  SITE_DEFAULT_TITLE,
  SITE_DEFAULT_DESCRIPTION,
  SITE_OG_IMAGE,
} from '../config/site';
import { jsonLd } from './schemas';

type SEOProps = {
  title?: string;
  description?: string;
  /** Caminho relativo (ex.: "/servicos/lgpd") ou URL absoluta. */
  canonical?: string;
  /** Caminho ou URL absoluta para a imagem og. */
  ogImage?: string;
  /** Array de schemas JSON-LD para injetar como <script type="application/ld+json">. */
  schemas?: unknown[];
  /** Se true, desliga indexação. Útil em páginas de busca/teste. Default: false. */
  noindex?: boolean;
};

function toAbsolute(maybePath: string): string {
  if (maybePath.startsWith('http')) return maybePath;
  const base = SITE_URL.replace(/\/$/, '');
  const path = maybePath.startsWith('/') ? maybePath : `/${maybePath}`;
  return `${base}${path}`;
}

export function SEO({
  title,
  description,
  canonical,
  ogImage,
  schemas = [],
  noindex = false,
}: SEOProps) {
  const finalTitle = title ?? SITE_DEFAULT_TITLE;
  const finalDescription = description ?? SITE_DEFAULT_DESCRIPTION;
  const finalCanonical = toAbsolute(canonical ?? '/');
  const finalOg = toAbsolute(ogImage ?? SITE_OG_IMAGE);

  return (
    <Helmet prioritizeSeoTags>
      <title>{finalTitle}</title>
      <meta name="description" content={finalDescription} />
      <link rel="canonical" href={finalCanonical} />
      {noindex && <meta name="robots" content="noindex, nofollow" />}

      <meta property="og:type" content="website" />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:title" content={finalTitle} />
      <meta property="og:description" content={finalDescription} />
      <meta property="og:url" content={finalCanonical} />
      <meta property="og:image" content={finalOg} />
      <meta property="og:locale" content="pt_BR" />

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={finalTitle} />
      <meta name="twitter:description" content={finalDescription} />
      <meta name="twitter:image" content={finalOg} />

      {schemas.map((schema, i) => (
        <script key={i} type="application/ld+json">
          {jsonLd(schema)}
        </script>
      ))}
    </Helmet>
  );
}
